import React, { useState, useEffect } from 'react';
import { Modal, Spin, Alert, Button, Space, Typography, message } from 'antd';
import { DownloadOutlined, PrinterOutlined, CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { StorageContainerLabelData } from '../types/storageLabel';
import { storageLabelService } from '../services/storageLabelService';
import { downloadBlob, generateLabelFilename, handleLabelError } from '../utils/labelUtils';

const { Text } = Typography;

// 容器编码最后一个"-"之后的序号部分用黑底白字反色块突出显示，与实际打印的标签效果保持一致
const renderHighlightedSystemCode = (code: string) => {
  const lastDashIndex = code.lastIndexOf('-');
  if (lastDashIndex === -1) {
    return <Text code>{code}</Text>;
  }
  const prefixPart = code.slice(0, lastDashIndex + 1);
  const sequencePart = code.slice(lastDashIndex + 1);
  return (
    <Text code>
      {prefixPart}
      <span style={{ backgroundColor: '#000', color: '#fff', padding: '0 4px', borderRadius: 2 }}>
        {sequencePart}
      </span>
    </Text>
  );
};

// 复制完整的容器编码：直接复制编码字符串本身，反色块的内边距只是样式，不会带进剪贴板
const copySystemCode = async (code: string) => {
  const text = String(code || '').trim();
  if (!text) return;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
    } else {
      // 非 https 环境下 clipboard API 不可用，退回到临时文本框复制
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (!ok) throw new Error('execCommand copy failed');
    }
    message.success(`已复制容器编码：${text}`);
  } catch {
    message.error('复制失败，请手动选择复制');
  }
};

const renderCopyCodeButton = (code: string) => (
  <Button
    type="text"
    size="small"
    title="复制容器编码"
    icon={<CopyOutlined />}
    onClick={() => copySystemCode(code)}
  />
);

interface StorageLabelPreviewProps {
  visible: boolean;
  data?: StorageContainerLabelData;
  onClose: () => void;
  onPrint?: (data: StorageContainerLabelData) => Promise<void>;
  onDownload?: (data: StorageContainerLabelData) => Promise<void>;
  /** 这个标签来自哪个临时分拣仓位（扫描入库页"生成-打印 新仓储容器"传入）；传了才在信息区右半边大字显示，不传和原来一样 */
  temporaryLocation?: number | string;
}

/**
 * 仓储标签预览组件
 * 专为admin-staff后台使用
 */
const StorageLabelPreview: React.FC<StorageLabelPreviewProps> = ({
  visible,
  data,
  onClose,
  onPrint,
  onDownload,
  temporaryLocation
}) => {
  const hasTemporaryLocation = temporaryLocation !== undefined && temporaryLocation !== null && String(temporaryLocation).trim() !== '';
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isPDF, setIsPDF] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // 生成预览
  useEffect(() => {
    if (visible && data) {
      generatePreview();
    } else {
      // 清理预览URL
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        setPreviewUrl('');
      }
    }

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [visible, data]);

  const generatePreview = async () => {
    if (!data) return;

    setLoading(true);
    setError('');

    try {
      console.log('[标签预览] 开始生成预览:', data);
      const response = await storageLabelService.generatePreview(data);
      setPreviewUrl(response.url);
      setIsPDF(response.isPDF);
      console.log('[标签预览] 预览生成成功:', response);
    } catch (err) {
      const errorInfo = handleLabelError(err, '预览生成');
      setError(`${errorInfo.message}: ${errorInfo.details}`);
      console.error('[标签预览] 预览生成失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async () => {
    if (!data) return;

    setPrinting(true);
    try {
      if (onPrint) {
        await onPrint(data);
      } else {
        // 使用默认打印逻辑
        const result = await storageLabelService.printLabel(data);
        if (result.success) {
          console.log('[标签预览] 打印成功:', result);
        } else {
          throw new Error(result.message);
        }
      }
    } catch (err) {
      const errorInfo = handleLabelError(err, '打印');
      setError(`${errorInfo.message}: ${errorInfo.details}`);
    } finally {
      setPrinting(false);
    }
  };

  const handleDownload = async () => {
    if (!data) return;

    setDownloading(true);
    try {
      if (onDownload) {
        await onDownload(data);
      } else {
        // 使用默认下载逻辑
        const blob = await storageLabelService.downloadLabel(data);
        const filename = generateLabelFilename(data);
        downloadBlob(blob, filename);
        console.log('[标签预览] 下载成功:', filename);
      }
    } catch (err) {
      const errorInfo = handleLabelError(err, '下载');
      setError(`${errorInfo.message}: ${errorInfo.details}`);
    } finally {
      setDownloading(false);
    }
  };

  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
    }
    setError('');
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <Text strong>仓储容器标签预览</Text>
          {data && (
            <>
              <Text type="secondary">-</Text>
              {renderHighlightedSystemCode(data.containerInfo.systemCode)}
              {renderCopyCodeButton(data.containerInfo.systemCode)}
            </>
          )}
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={800}
      footer={[
        <Button key="close" onClick={handleClose} icon={<CloseOutlined />}>
          关闭
        </Button>,
        <Button 
          key="download" 
          onClick={handleDownload}
          loading={downloading}
          icon={<DownloadOutlined />}
        >
          下载
        </Button>,
        <Button 
          key="print" 
          type="primary"
          onClick={handlePrint}
          loading={printing}
          icon={<PrinterOutlined />}
        >
          打印
        </Button>
      ]}
      maskClosable={false}
      destroyOnClose
    >
      <div style={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
        {/* 容器信息摘要 */}
        {data && (
          <div style={{
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: '#f5f5f5',
            borderRadius: '6px',
            ...(hasTemporaryLocation ? { display: 'flex', alignItems: 'stretch', gap: 12 } : {})
          }}>
            <Space direction="vertical" size="small" style={{ width: '100%', ...(hasTemporaryLocation ? { flex: '1 1 50%', minWidth: 0 } : {}) }}>
              <div>
                <Text strong>容器编码：</Text>
                {renderHighlightedSystemCode(data.containerInfo.systemCode)}
                {renderCopyCodeButton(data.containerInfo.systemCode)}
              </div>
              <div>
                <Text strong>备注：</Text>
                <Text>{String(data.containerInfo.remarks || '-')}</Text>
              </div>
              <div>
                <Text strong>项目：</Text>
                {(() => {
                  const code = String(data.containerInfo.project.code || '').trim();
                  const rawName = String(data.containerInfo.project.name || '').trim();
                  const name = (() => {
                    if (!code || !rawName) return rawName;
                    if (rawName === code) return '';
                    if (rawName.startsWith(`${code} `)) return rawName.slice(code.length).trim();
                    if (rawName.startsWith(`${code}-`)) return rawName.slice(code.length + 1).trim();
                    if (rawName.startsWith(`${code}_`)) return rawName.slice(code.length + 1).trim();
                    if (rawName.startsWith(`${code}(`)) return rawName.slice(code.length).trim();
                    return rawName;
                  })();
                  if (code && name) {
                    return (
                      <Text>
                        <span style={{ color: '#1677ff', fontWeight: 400 }}>{code}</span>
                        {' '}
                        <span>{name}</span>
                      </Text>
                    );
                  }
                  if (code) {
                    return <Text><span style={{ color: '#1677ff', fontWeight: 400 }}>{code}</span></Text>;
                  }
                  if (name) {
                    return <Text>{name}</Text>;
                  }
                  return <Text>-</Text>;
                })()}
              </div>
              {data.contentSummary && (
                <div>
                  <Text strong>内容：</Text>
                  <Text>
                    {data.contentSummary.totalItems}项 / {data.contentSummary.totalQuantity}个
                  </Text>
                </div>
              )}
            </Space>
            {hasTemporaryLocation && (
              <div className="label-preview-temporary-location" style={{ flex: '1 1 50%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Text strong>临时分拣仓位</Text>
                <span
                  className="label-preview-temporary-location-value"
                  style={{
                    display: 'inline-block',
                    fontSize: '88px',
                    fontWeight: 'bold',
                    color: '#cf1322',
                    padding: '0 24px',
                    borderRadius: 8,
                    background: '#ffeb3b',
                    border: '1px solid #fadb14',
                    lineHeight: 1.1,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {String(temporaryLocation)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* 错误显示 */}
        {error && (
          <Alert
            message="预览失败"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
            action={
              <Button size="small" onClick={generatePreview}>
                重试
              </Button>
            }
          />
        )}

        {/* 加载状态 */}
        {loading && (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center' 
          }}>
            <Spin size="large" tip="正在生成预览..." />
          </div>
        )}

        {/* 预览内容 */}
        {!loading && !error && previewUrl && (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            border: '1px solid #d9d9d9',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            {isPDF ? (
              <iframe
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '400px',
                  border: 'none'
                }}
                title="标签预览"
              />
            ) : (
              <img
                src={previewUrl}
                alt="标签预览"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain'
                }}
              />
            )}
          </div>
        )}

        {/* 空状态 */}
        {!loading && !error && !previewUrl && (
          <div style={{ 
            flex: 1, 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            color: '#999'
          }}>
            暂无预览内容
          </div>
        )}
      </div>
    </Modal>
  );
};

export default StorageLabelPreview;
